import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * An inline message about the thing next to it — not a toast, which interrupts,
 * and not a dialog, which blocks.
 *
 * Three of these were previously written by hand at three call sites, each with
 * its own border colour, padding and icon size. They are one component now, and
 * the icon is chosen by the variant rather than passed in, because "warning
 * with a checkmark" should not be expressible.
 */
const alertVariants = cva('flex gap-3 rounded-lg border p-3.5', {
  variants: {
    variant: {
      info: 'border-border bg-surface-muted text-foreground-secondary [&>svg]:text-foreground-muted',
      accent:
        'border-accent-subtle-border bg-accent-subtle text-foreground-secondary [&>svg]:text-accent',
      success:
        'border-success-subtle-border bg-success-subtle text-foreground-secondary [&>svg]:text-success',
      warning:
        'border-warning-subtle-border bg-warning-subtle text-foreground-secondary [&>svg]:text-warning',
      destructive:
        'border-destructive-subtle-border bg-destructive-subtle text-foreground-secondary [&>svg]:text-destructive',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

const VARIANT_ICON = {
  info: Info,
  accent: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  destructive: CircleAlert,
} as const;

function Alert({
  className,
  variant,
  title,
  icon,
  children,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    title?: string;
    /** Overrides the variant's icon where the context has a more specific one
        — a "sent back" notice reads better with an arrow than a warning sign. */
    icon?: React.ComponentType<{ className?: string }>;
  }) {
  const Icon = icon ?? VARIANT_ICON[variant ?? 'info'];

  return (
    <div
      data-slot="alert"
      role={variant === 'destructive' ? 'alert' : 'status'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <Icon className="mt-px size-4 shrink-0" />
      <div className="type-body min-w-0 flex-1 space-y-0.5">
        {title && <p className="font-medium text-foreground">{title}</p>}
        {children}
      </div>
    </div>
  );
}

export { Alert, alertVariants };
